import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { Error as SignupError } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import GoogleLogo from "@/components/GoogleLogo";
import { useSignupPasswordManager } from "../hooks/useSignupPasswordManager";
import { getFieldError } from "../utils/helpers/errorUtils";
import { rebaseLinkToCurrentOrigin } from "../utils/helpers/urlUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

interface SignUpPasswordFormData {
  password: string;
  captcha?: string;
}

function SignUpPasswordScreen() {
  const {
    handleSignupPassword,
    handleFederatedSignup,
    errors,
    texts,
    passwordPolicy,
    alternateConnections,
    loginLink,
    editIdentifierLink,
    isCaptchaAvailable,
    captchaImage,
    identifierData,
  } = useSignupPasswordManager();

  const form = useForm<SignUpPasswordFormData>({
    defaultValues: {
      password: "",
      captcha: "",
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors: formErrors, isSubmitting },
  } = form;

  const [showPasswords, setShowPasswords] = useState(false);
  const passwordValue = watch("password") || "";

  const sdkErrors = (errors || []) as SignupError[];
  const generalErrors = sdkErrors.filter((error) => !error.field);
  const passwordSDKError = getFieldError("password", sdkErrors);
  const captchaSDKError = getFieldError("captcha", sdkErrors);

  const headingText =
    texts?.title || texts?.heading || "Secure your new account";
  const loginActionText =
    texts?.loginActionText || texts?.loginActionCta || "Already have an account?";
  const loginLinkText = texts?.loginActionLinkText || "Sign in";
  const buttonText = texts?.buttonText || "Create account";
  const passwordLabel = (texts?.passwordPlaceholder || "Password").concat("*");
  const captchaLabel = (texts?.captchaCodePlaceholder || "CAPTCHA").concat("*");
  const localizedLoginLink =
    loginLink && rebaseLinkToCurrentOrigin(loginLink as string);
  const localizedEditIdentifierLink =
    editIdentifierLink && rebaseLinkToCurrentOrigin(editIdentifierLink as string);

  type ConnectionLike = {
    name?: string;
    options?: { displayName?: string };
    metadata?: { displayName?: string };
  };

  const getConnectionLabel = (connection: ConnectionLike) =>
    connection.options?.displayName ||
    connection.metadata?.displayName ||
    connection.name;

  const isGoogleConnection = (connection: ConnectionLike) => {
    const connectionName = connection.name?.toLowerCase();
    const connectionLabel = getConnectionLabel(connection)?.toLowerCase();
    return (
      connectionName === "google-oauth2" ||
      (connectionLabel ? connectionLabel.includes("google") : false)
    );
  };

  const isLinkedInConnection = (connection: ConnectionLike) => {
    const connectionName = connection.name?.toLowerCase();
    const connectionLabel = getConnectionLabel(connection)?.toLowerCase();
    return (
      connectionName === "linkedin" ||
      connectionName === "linkedin-oauth2" ||
      (connectionLabel ? connectionLabel.includes("linkedin") : false)
    );
  };

  const visibleConnections = alternateConnections.filter(
    (connection) => !isLinkedInConnection(connection as ConnectionLike)
  );

  const passwordChecks = useMemo(() => {
    const minLength = passwordPolicy?.minLength ?? 8;
    const lengthOk = passwordValue.length >= minLength;
    const hasLower = /[a-z]/.test(passwordValue);
    const hasUpper = /[A-Z]/.test(passwordValue);
    const hasNumber = /[0-9]/.test(passwordValue);
    const hasSpecial = /[^A-Za-z0-9]/.test(passwordValue);
    const diversityCount = [hasLower, hasUpper, hasNumber, hasSpecial].filter(
      Boolean
    ).length;
    const diversityOk = diversityCount >= 3;

    return [
      {
        id: "length",
        label: `At least ${minLength} characters`,
        met: lengthOk,
      },
      { id: "diversity", label: "At least 3 of the following:", met: diversityOk },
      { id: "lower", label: "Lower case letters (a-z)", met: hasLower },
      { id: "upper", label: "Upper case letters (A-Z)", met: hasUpper },
      { id: "number", label: "Numbers (0-9)", met: hasNumber },
      {
        id: "special",
        label: "Special characters (e.g. !@#$%^&*)",
        met: hasSpecial,
      },
    ];
  }, [passwordValue, passwordPolicy]);

  const identifierSummary = useMemo(() => {
    const items: Array<{ label: string; value: string }> = [];
    if (identifierData.email) {
      items.push({
        label: texts?.emailPlaceholder || "Email",
        value: identifierData.email,
      });
    }
    // if (identifierData.username) {
    //   items.push({
    //     label: texts?.usernameOnlyPlaceholder || "Username",
    //     value: identifierData.username,
    //   });
    // }
    // if (identifierData.phoneNumber) {
    //   items.push({
    //     label: texts?.phonePlaceholder || "Phone number",
    //     value: identifierData.phoneNumber,
    //   });
    // }
    return items;
  }, [identifierData, texts]);

  const onSubmit = async (data: SignUpPasswordFormData) => {
    await handleSignupPassword(data.password, data.captcha);
  };

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <form
          className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-0 flex flex-col items-start text-left">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">
              {headingText}
            </p>
            {identifierSummary.length > 0 && (
              <div className="mt-4 w-full rounded-none border border-border bg-sand-050 px-5 py-4 text-left text-sm text-ink-2">
                <div className="flex items-center justify-end text-xs font-semibold uppercase tracking-[0.3em] text-ink-3">
                  {/* <span>{texts?.identifierSummaryTitle || "Your details"}</span> */}
                  {localizedEditIdentifierLink && (
                    <a
                      href={localizedEditIdentifierLink}
                      className="tracking-normal text-xs font-semibold text-ink-2 underline underline-offset-2 hover:text-ink"
                    >
                      {texts?.editIdentifierText || "Edit"}
                    </a>
                  )}
                </div>
                <dl className="mt-4 space-y-2 text-sm">
                  {identifierSummary.map((item) => (
                    <div key={item.label} className="flex flex-col">
                      <dt className="text-xs font-semibold uppercase tracking-[0.25em] text-ink-3">
                        {item.label}
                      </dt>
                      <dd className="text-base font-medium text-ink">
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`signuppassword-general-error-${index}`}>
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-semibold text-ink-2" htmlFor="password">
              {passwordLabel}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPasswords ? "text" : "password"}
                placeholder={texts?.passwordPlaceholder || "********"}
                autoComplete="new-password"
                className="input-field pr-12 tracking-[0.3em]"
                aria-invalid={
                  !!formErrors.password?.message ||
                  !!passwordSDKError ||
                  undefined
                }
                {...register("password", {
                  required: "Password is required",
                  minLength: passwordPolicy?.minLength
                    ? {
                        value: passwordPolicy.minLength,
                        message: `Password must be at least ${passwordPolicy.minLength} characters`,
                      }
                    : undefined,
                })}
              />
              <button
                type="button"
                aria-pressed={showPasswords}
                onClick={() => setShowPasswords((prev) => !prev)}
                className="absolute inset-y-0 right-3 text-xs font-semibold text-ink-2 hover:text-ink"
              >
                {showPasswords ? "Hide" : "Show"}
                <span className="sr-only"> password</span>
              </button>
            </div>
            {(formErrors.password?.message || passwordSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.password?.message || passwordSDKError}
              </p>
            )}
          </div>

          {passwordValue.length > 0 && (
            <div className="rounded-none border border-border bg-sand-050 px-5 py-4 text-sm text-ink-2">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-3">
                {texts?.passwordHintTitle || "Your password must contain"}
              </p>
              <ul className="mt-4 space-y-2">
                {passwordChecks.map((rule) => {
                  const isSubRule = ["lower", "upper", "number", "special"].includes(
                    rule.id
                  );
                  return (
                    <li
                      key={rule.id}
                      className={`flex items-start gap-3 text-sm ${
                        rule.met ? "text-success" : "text-ink-2"
                      } ${isSubRule ? "pl-6" : ""}`}
                    >
                      <span className="mt-1 inline-flex h-3.5 w-3.5 items-center justify-center">
                        {rule.met ? (
                          <svg
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                            className="h-3.5 w-3.5 text-success"
                          >
                            <path
                              fill="currentColor"
                              d="M6.6 11.1 3.5 8l1-1 2.1 2.1L11.5 4l1 1z"
                            />
                          </svg>
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-ink-3" />
                        )}
                      </span>
                      <span>{rule.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {isCaptchaAvailable && (
            <div className="space-y-3">
              <label
                htmlFor="captcha"
                className="text-sm font-semibold text-ink-2"
              >
                {captchaLabel}
              </label>
              {captchaImage && (
                <img
                  src={captchaImage}
                  alt="CAPTCHA challenge"
                  className="h-16 w-full rounded-none border border-border object-contain"
                />
              )}
              <input
                id="captcha"
                type="text"
                placeholder={texts?.captchaCodePlaceholder || "Enter the code"}
                className="input-field"
                aria-invalid={
                  !!formErrors.captcha?.message || !!captchaSDKError || undefined
                }
                {...register("captcha", {
                  required: "Please complete the CAPTCHA",
                  maxLength: {
                    value: 15,
                    message: "CAPTCHA code is too long",
                  },
                })}
              />
              {(formErrors.captcha?.message || captchaSDKError) && (
                <p className="text-sm text-danger">
                  {formErrors.captcha?.message || captchaSDKError}
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Please wait..." : buttonText}
          </button>

          <p className="text-sm text-ink-2">
            Problems signing in?{" "}
            <a
              href="https://www.butterfield.com/my-account-faq"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer hover:text-ink"
            >
              Click here
            </a>
            .
          </p>

          {visibleConnections.length > 0 && (
            <>
              <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-ink-3">
                <span className="h-px flex-1 bg-border" aria-hidden />
                <span>{texts?.separatorText || "or"}</span>
                <span className="h-px flex-1 bg-border" aria-hidden />
              </div>

              <div className="space-y-3">
                {visibleConnections.map((connection) => {
                  const enhancedConnection = connection as ConnectionLike;

                  const connectionLabel = getConnectionLabel(enhancedConnection);
                  const connectionCta = isGoogleConnection(enhancedConnection)
                    ? "Continue with Google"
                    : connectionLabel;
                  const showGoogleLogo = isGoogleConnection(enhancedConnection);

                  return (
                    <button
                      key={enhancedConnection.name}
                      type="button"
                      onClick={() =>
                        enhancedConnection.name &&
                        handleFederatedSignup(enhancedConnection.name)
                      }
                      className="flex w-full items-center justify-center gap-3 rounded-sm border border-border bg-white px-4 py-3 text-base font-semibold text-ink transition hover:cursor-pointer hover:bg-btn-hover hover:shadow-sm"
                    >
                      {showGoogleLogo && (
                        <span className="inline-flex items-center justify-center rounded-full bg-white">
                          <GoogleLogo className="h-5 w-5" />
                        </span>
                      )}
                      {connectionCta}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <p className="text-sm text-ink-2">
            {loginActionText}{" "}
            {localizedLoginLink ? (
              <a
                href={localizedLoginLink}
                className="font-semibold text-ink underline underline-offset-2"
              >
                {loginLinkText}
              </a>
            ) : (
              <span className="font-semibold text-ink">{loginLinkText}</span>
            )}
            .
          </p>
        </form>
      </div>
    </div>
  );
}

export default SignUpPasswordScreen;
